export type IcpFitLevel =
  | 'strong'
  | 'moderate'
  | 'emerging'
  | 'low'
  | 'insufficient_data'

export type IcpQualificationResultLike = {
  icpId?: string
  icp_id?: string
  icpName?: string
  icp_name?: string
  qualified?: boolean
  qualificationLevel?: string
  qualification_level?: string
  score?: number
  matchMessages?: string[]
  match_messages?: string[]
  fitLevel?: string
  fit_level?: string
  fitScore?: number
  fit_score?: number
  icpFit?: number
  icp_fit?: number
}

export const FIT_LEVEL_PRIORITY: Record<IcpFitLevel, number> = {
  strong: 5,
  moderate: 4,
  emerging: 3,
  low: 2,
  insufficient_data: 1,
}

/** All accepted API strings per fit level, including underscore forms like insufficient_data. */
export const FIT_LEVEL_LABEL_VARIANTS: Record<IcpFitLevel, readonly string[]> =
  {
    strong: ['strong', 'strong_fit', 'strong fit'],
    moderate: ['moderate', 'moderate_fit', 'moderate fit'],
    emerging: ['emerging', 'emerging_fit', 'emerging fit'],
    low: ['low', 'low_fit', 'low fit'],
    insufficient_data: [
      'insufficient_data',
      'insufficient data',
      'insufficient_data_fit',
      'insufficient data fit',
    ],
  }

const FIT_LEVEL_BASES = Object.keys(FIT_LEVEL_LABEL_VARIANTS) as IcpFitLevel[]

export const FIT_LEVEL_ALIASES: Record<string, IcpFitLevel> =
  Object.fromEntries(
    Object.entries(FIT_LEVEL_LABEL_VARIANTS).flatMap(([level, variants]) =>
      variants.map(variant => [variant, level as IcpFitLevel])
    )
  ) as Record<string, IcpFitLevel>

const toFitLevelCandidateKeys = (value: string): string[] => {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ')
  const withoutFitSuffix = normalized.replace(/\s+fit$/, '')

  return [
    normalized,
    normalized.replace(/\s/g, '_'),
    withoutFitSuffix,
    withoutFitSuffix.replace(/\s/g, '_'),
  ]
}

export const normalizeIcpFitLevel = (
  value?: string | null
): IcpFitLevel | null => {
  if (!value) return null

  for (const candidate of toFitLevelCandidateKeys(value)) {
    if (FIT_LEVEL_ALIASES[candidate]) {
      return FIT_LEVEL_ALIASES[candidate]
    }

    if (FIT_LEVEL_BASES.includes(candidate as IcpFitLevel)) {
      return candidate as IcpFitLevel
    }
  }

  return null
}

export const ICP_FIT_LEVEL_LABELS: Record<IcpFitLevel, string> = {
  strong: 'Strong fit',
  moderate: 'Moderate fit',
  emerging: 'Emerging fit',
  low: 'Low fit',
  insufficient_data: 'Insufficient data',
}

/** Drawer hero chip labels — include "ICP" in the fit phrase. */
export const ICP_FIT_LEVEL_DRAWER_LABELS: Record<IcpFitLevel, string> = {
  strong: 'Strong ICP fit',
  moderate: 'Moderate ICP fit',
  emerging: 'Emerging ICP fit',
  low: 'Low ICP fit',
  insufficient_data: 'Insufficient data',
}

export const ICP_FIT_LEVEL_QUERY_VALUES: Record<IcpFitLevel, string> = {
  strong: 'Strong fit',
  moderate: 'Moderate fit',
  emerging: 'Emerging fit',
  low: 'Low fit',
  insufficient_data: 'Insufficient data',
}

export const getIcpFitLevelFromScore = (score: number): IcpFitLevel => {
  if (score >= 80) return 'strong'
  if (score >= 60) return 'moderate'
  if (score >= 40) return 'emerging'
  if (score >= 20) return 'low'
  return 'insufficient_data'
}

export const getIcpResultFitLevel = (
  result: IcpQualificationResultLike
): IcpFitLevel | null => {
  const fitLevel = normalizeIcpFitLevel(
    result.qualificationLevel ??
      result.qualification_level ??
      result.fitLevel ??
      result.fit_level
  )
  if (fitLevel) return fitLevel

  const score =
    result.score ??
    result.fitScore ??
    result.fit_score ??
    result.icpFit ??
    result.icp_fit
  if (typeof score === 'number' && !Number.isNaN(score)) {
    return getIcpFitLevelFromScore(score)
  }

  return null
}

export const getBestIcpFitLevel = (
  levels: Array<IcpFitLevel | null | undefined>
): IcpFitLevel | null => {
  const validLevels = levels.filter((level): level is IcpFitLevel =>
    Boolean(level)
  )
  if (!validLevels.length) return null

  return validLevels.reduce((best, current) =>
    FIT_LEVEL_PRIORITY[current] > FIT_LEVEL_PRIORITY[best] ? current : best
  )
}

export const resolvePrimaryIcpFitLevel = ({
  icpFitLevel,
  icpFit,
  icpQualificationResults = [],
}: {
  icpFitLevel?: string | null
  icpFit?: number | null
  icpQualificationResults?: IcpQualificationResultLike[]
}): IcpFitLevel | null => {
  const rowLevel =
    normalizeIcpFitLevel(icpFitLevel) ??
    (typeof icpFit === 'number' ? getIcpFitLevelFromScore(icpFit) : null)

  const resultLevels = icpQualificationResults.map(getIcpResultFitLevel)
  const bestResultLevel = getBestIcpFitLevel(resultLevels)

  if (rowLevel && bestResultLevel) {
    return getBestIcpFitLevel([rowLevel, bestResultLevel])
  }

  if (rowLevel || bestResultLevel) {
    return rowLevel ?? bestResultLevel
  }

  if (!icpQualificationResults.length) return null

  const hasQualified = icpQualificationResults.some(
    result => result.qualified === true
  )
  return hasQualified ? 'moderate' : 'low'
}

export const getAdditionalIcpCount = (totalCount: number): number =>
  Math.max(0, totalCount - 1)

export const getIcpResultName = (result: IcpQualificationResultLike): string =>
  result.icpName ?? result.icp_name ?? ''

export const getIcpResultId = (result: IcpQualificationResultLike): string =>
  String(result.icpId ?? result.icp_id ?? '').trim()

export const resolvePrimaryIcpResult = (
  icpResults: IcpQualificationResultLike[],
  primaryFitLevel: IcpFitLevel | null
): IcpQualificationResultLike | null => {
  if (!icpResults.length) return null

  if (primaryFitLevel) {
    const matched = icpResults.find(
      result => getIcpResultFitLevel(result) === primaryFitLevel
    )
    if (matched) return matched
  }

  return icpResults[0] ?? null
}

export const resolvePrimaryIcpMatchMessages = (
  icpResults: IcpQualificationResultLike[],
  primaryFitLevel: IcpFitLevel
): string[] => {
  if (!icpResults.length) return []

  const primaryResult =
    icpResults.find(result => {
      const level = normalizeIcpFitLevel(
        result.qualificationLevel ?? result.qualification_level
      )
      return level === primaryFitLevel
    }) ?? icpResults[0]

  return (
    primaryResult.matchMessages ??
    primaryResult.match_messages ??
    []
  ).filter(Boolean)
}
